import type { JSX, UIEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown } from 'lucide-react'
import type { LogLevel } from '@apiary/shared'
import type { BotEventDto } from '@/lib/dto'
import { cn } from '@/lib/cn'
import { clockOf } from '@/ui/format'
import { Button } from '@/ui/primitives/Button'

/**
 * Поток строк лога: виртуализированный список с «липким» автоскроллом.
 *
 * Липкий автоскролл — главное поведение живой консоли, и он устроен так:
 *
 *  - пока пользователь у нижней кромки (в пределах `STICKY_EPSILON`), новые
 *    строки прокручиваются автоматически;
 *  - стоит отскроллить вверх — автоскролл ВЫКЛЮЧАЕТСЯ. Это не мелочь: без
 *    выключения чтение строки, из-за которой человек и полез в логи, невозможно
 *    физически — список утаскивает её вверх на каждом новом событии;
 *  - пока автоскролл выключен, копится счётчик непрочитанных, и появляется
 *    кнопка «вниз, N новых» — единственный способ вернуться, не догадываясь;
 *  - возврат к низу колесом тоже включает автоскролл обратно: явного действия
 *    здесь не требуют, потому что «я внизу» и есть согласие следить дальше.
 *
 * Виртуализация обязательна: буфер держит до тысячи строк (`LOG_BUFFER_LIMIT`), а
 * DOM на тысячу узлов делает скролл рваным даже без анимаций.
 */

/** Насколько можно отойти от низа, чтобы автоскролл всё ещё считался активным. */
export const STICKY_EPSILON = 24
/** Оценка высоты строки: 13px mono × 1.4 + вертикальные отступы. */
const ROW_HEIGHT = 22

const LEVEL_TEXT: Record<LogLevel, string> = {
  DEBUG: 'text-ink-faint',
  INFO: 'text-ink-muted',
  CHAT: 'text-busy',
  WARN: 'text-warn',
  ERROR: 'text-fail'
}

/** Полоска слева от строки: второй, невербальный носитель уровня. */
const LEVEL_EDGE: Record<LogLevel, string> = {
  DEBUG: 'bg-transparent',
  INFO: 'bg-transparent',
  CHAT: 'bg-busy/60',
  WARN: 'bg-warn/70',
  ERROR: 'bg-fail/80'
}

export interface LogStreamProps {
  rows: readonly BotEventDto[]
  /** Переносить длинные строки вместо обрезки. Меняет режим измерения высоты. */
  wrap?: boolean
  /** Текст пустого состояния — экран знает, пусто «совсем» или «по фильтру». */
  empty: string
  className?: string
}

export function LogStream({ rows, wrap = false, empty, className }: LogStreamProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [sticky, setSticky] = useState(true)
  const [unseen, setUnseen] = useState(0)
  const seen = useRef(rows.length)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    // С переносом строк высота заранее неизвестна — включаем измерение по факту.
    // Без переноса все строки ровно `ROW_HEIGHT`, и измерять нечего.
    overscan: 24
  })

  const toBottom = useCallback((): void => {
    const element = scrollRef.current
    if (element === null) return
    element.scrollTop = element.scrollHeight
  }, [])

  // Реакция на приход строк. `useLayoutEffect` — чтобы прокрутка случилась в том
  // же кадре, что и вставка: иначе виден рывок «строка появилась → уехала».
  useLayoutEffect(() => {
    const added = rows.length - seen.current
    seen.current = rows.length
    if (added <= 0) return
    if (sticky) {
      toBottom()
      return
    }
    setUnseen(previous => previous + added)
  }, [rows.length, sticky, toBottom])

  // Первый кадр и смена фильтра: если автоскролл включён, показываем хвост.
  useEffect(() => {
    if (sticky) toBottom()
  }, [sticky, toBottom, rows])

  // Смена режима переноса меняет высоту КАЖДОЙ строки, а кэш измерений об этом
  // не знает — без сброса список остаётся с высотами прошлого режима.
  useEffect(() => {
    virtualizer.measure()
  }, [wrap, virtualizer])

  const onScroll = (event: UIEvent<HTMLDivElement>): void => {
    const element = event.currentTarget
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight
    const atBottom = distance <= STICKY_EPSILON
    setSticky(atBottom)
    if (atBottom) setUnseen(0)
  }

  const resume = (): void => {
    setSticky(true)
    setUnseen(0)
    toBottom()
  }

  const items = virtualizer.getVirtualItems()

  return (
    <div className={cn('relative flex min-h-0 flex-col', className)}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        role="log"
        aria-label="Консоль логов бота"
        tabIndex={0}
        data-testid="log-scroll"
        className="h-full min-h-40 overflow-y-auto rounded-sm border border-line bg-abyss/60 py-1.5 font-mono text-sm leading-log"
      >
        {rows.length === 0 ? (
          <p className="py-10 text-center font-sans text-base text-ink-muted">{empty}</p>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {items.map(item => {
              const event = rows[item.index]
              if (event === undefined) return null
              return (
                <div
                  key={event.id}
                  data-index={item.index}
                  ref={wrap ? virtualizer.measureElement : undefined}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${item.start}px)`,
                    ...(wrap ? {} : { height: ROW_HEIGHT })
                  }}
                >
                  <LogRow event={event} wrap={wrap} />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Кнопка возврата живёт НАД списком и только когда автоскролл выключен:
          постоянная кнопка «вниз» в консоли, которая и так внизу, — шум. */}
      {sticky ? null : (
        <Button
          size="sm"
          variant="secondary"
          onClick={resume}
          className="anim-pop absolute right-4 bottom-4 shadow-md"
        >
          <ArrowDown aria-hidden className="size-3.5" />
          {unseen === 0 ? 'Вниз' : `Вниз, ${unseen} ${plural(unseen)}`}
        </Button>
      )}

      <p
        className="mt-1.5 shrink-0 text-2xs text-ink-faint"
        data-testid="log-autoscroll-state"
        role="status"
      >
        {sticky
          ? 'Автоскролл включён — новые строки видны сразу'
          : 'Автоскролл на паузе: вы читаете историю'}
      </p>
    </div>
  )
}

/** `1 новая` / `2 новые` / `5 новых` — иначе счётчик читается как машинный. */
function plural(count: number): string {
  const tail = count % 100
  if (tail > 10 && tail < 20) return 'новых'
  const last = count % 10
  if (last === 1) return 'новая'
  if (last >= 2 && last <= 4) return 'новые'
  return 'новых'
}

function LogRow({ event, wrap }: { event: BotEventDto; wrap: boolean }): JSX.Element {
  return (
    <div
      data-testid="log-row"
      className={cn(
        'flex items-start gap-2 pr-3 pl-0 hover:bg-panel-2/60',
        wrap ? 'py-0.5' : 'h-full items-center'
      )}
    >
      <span
        aria-hidden
        className={cn('h-full w-0.5 shrink-0 rounded-pill', LEVEL_EDGE[event.level])}
      />
      <span className="shrink-0 pl-1.5 text-ink-faint tabular-nums">{clockOf(event.at)}</span>
      {/* Уровень подписан текстом: цвет не может быть единственным носителем. */}
      <span className={cn('w-11 shrink-0 font-medium', LEVEL_TEXT[event.level])}>
        {event.level}
      </span>
      <span className="w-20 shrink-0 truncate text-ink-faint" title={event.kind}>
        {event.kind}
      </span>
      <span
        className={cn('min-w-0 text-ink', wrap ? 'break-words whitespace-pre-wrap' : 'truncate')}
        title={wrap ? undefined : event.message}
      >
        {event.message}
      </span>
    </div>
  )
}
